/**
 * Deploy base-path helpers.
 *
 * In production the app is served under a sub-path (e.g.
 * "/hornsby-kangaroo-point/" via the shared launch-v4 router), so the live
 * pathname is "/hornsby-kangaroo-point/dev/targets" rather than "/dev/targets".
 * `import.meta.env.BASE_URL` is "/" in dev and the VITE_BASE_PATH prefix in
 * production.
 *
 * preact-router matches `window.location.pathname` verbatim and has no base
 * concept, so routes/links that must MATCH a specific path (the dev pages)
 * need the base prefix baked in. The main AR routes don't — they fall through
 * to the default route and resolve the artwork by pathname suffix.
 */

/** Deploy base path with no trailing slash. "" in dev, "/<base>" in prod. */
export const BASE_PATH = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");

/** Prefix an app-absolute path (leading "/") with the deploy base path. */
export const withBasePath = (path: string): string => `${BASE_PATH}${path}`;

/**
 * Strip the deploy base path off a pathname, yielding the app-relative path.
 * Used to test routes (e.g. "/dev/*") regardless of where the app is mounted.
 */
export const getRelativePath = (
  pathname: string = typeof window !== "undefined"
    ? window.location.pathname
    : "/",
): string => {
  if (BASE_PATH && pathname.startsWith(BASE_PATH)) {
    const rel = pathname.slice(BASE_PATH.length);
    return rel === "" ? "/" : rel.startsWith("/") ? rel : `/${rel}`;
  }
  return pathname;
};

/**
 * Patch `window.Worker` so web-core's hardcoded origin-root worker URLs
 * (`new URL('/js/<worker>.js', location.origin)` — e.g. the QR scanner's
 * `qrcodeWorker.js`) resolve under the deploy base path. Without this they
 * 404/502 on a sub-path deploy and the worker silently fails to load.
 *
 * Kept project-side (rather than editing the shared web-core submodule) and
 * scoped narrowly: only origin-root `/js/*.js` URLs are rewritten; everything
 * else (8thwall/Babylon blob workers, already-prefixed URLs) passes through
 * untouched. No-op at a domain root (BASE_PATH is "").
 *
 * Must run before the 8thwall/QR pipeline constructs its worker — call it from
 * the app entry (`main.tsx`).
 */
export const patchWorkerBasePath = (): void => {
  if (typeof window === "undefined" || !BASE_PATH) return;

  const NativeWorker = window.Worker;
  if ((NativeWorker as unknown as { __basePatched?: boolean }).__basePatched) {
    return;
  }

  class BasePathWorker extends NativeWorker {
    constructor(scriptURL: string | URL, options?: WorkerOptions) {
      const href = typeof scriptURL === "string" ? scriptURL : scriptURL.href;
      const match = /^(https?:\/\/[^/]+)?(\/js\/[^/?#]+\.js)([?#].*)?$/.exec(href);
      const finalUrl = match
        ? `${match[1] ?? window.location.origin}${BASE_PATH}${match[2]}${match[3] ?? ""}`
        : scriptURL;
      super(finalUrl, options);
    }
  }

  (BasePathWorker as unknown as { __basePatched: boolean }).__basePatched = true;
  window.Worker = BasePathWorker as unknown as typeof Worker;
};
